/**
 * Simple UUIDv7 generator and parser utilities
 */

/**
 * Generate a UUIDv7 (time-ordered UUID)
 * Format: xxxxxxxx-xxxx-7xxx-xxxx-xxxxxxxxxxxx
 * First 48 bits are Unix timestamp in milliseconds
 */
export function generateUuidV7(): string {
  const timestamp = Date.now();
  const timestampHex = timestamp.toString(16).padStart(12, '0');
  
  // Generate random bits for the rest
  const randomBytes = new Uint8Array(10);
  crypto.getRandomValues(randomBytes);
  
  // Convert to hex
  const randomHex = Array.from(randomBytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // Format as UUIDv7: tttttttt-tttt-7xxx-xxxx-xxxxxxxxxxxx
  const uuid = [
    timestampHex.slice(0, 8),      // 32 bits of timestamp
    timestampHex.slice(8, 12),     // 16 bits of timestamp
    '7' + randomHex.slice(0, 3),   // Version 7 + 12 bits random
    '8' + randomHex.slice(3, 6),   // Variant bits + 12 bits random
    randomHex.slice(6, 18)         // 48 bits random
  ].join('-');
  
  return uuid;
}

/**
 * Parse UUIDv7 to display format: YYYY-MM-DD/XXXX
 * Where YYYY-MM-DD is the date from the timestamp and XXXX is the last 4 chars
 * 
 * Example:
 * Input:  "01983ddc-d6c7-743a-a5cb-b53f2a9951ba"
 * Output: "2025-07-24/51ba"
 */
export const parseUuidV7 = (u: string): string => {
  try {
    const date = new Date(Number((BigInt('0x' + u.replace(/-/g, '')) >> 80n) & 0xffffffffffffn))
      .toISOString().slice(0, 10); // Keep the dashes in YYYY-MM-DD format
    const suffix = u.replace(/-/g, '').slice(-4);
    
    return `${date}/${suffix}`;
  } catch (error) {
    console.error('Error parsing UUIDv7:', error);
    // Fallback to showing last 8 characters if parsing fails
    return u.slice(-8);
  }
};

/**
 * Check if a string is a valid UUIDv7 format
 */
export function isUuidV7(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}